import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {object} BundleSlot
 * @property {string} handle - Product handle; how a saved bundle finds its card again.
 * @property {string} variantId
 * @property {string} title
 * @property {number} price - Unit price in minor units (cents).
 * @property {string} image
 * @property {string} capColor
 */

/**
 * @typedef {object} BundleBuilderRefs
 * @property {HTMLElement} slots - The slot row container.
 * @property {HTMLTemplateElement} filledTemplate - Filled-slot markup to clone.
 */

/**
 * Build-your-own-bundle: pick up to N products into slots, each with a "Kleur dop"
 * cap color, see a tiered running total, and add the whole bundle to cart in one
 * request. The total shown here is projected; the real discount is a per-tier
 * Shopify discount code that `bundle-discount.js` applies to the cart once these
 * lines land (see docs/bundle-tiers.md and docs/bundle-discount-codes.md).
 *
 * @extends {Component<BundleBuilderRefs>}
 */
class BundleBuilder extends Component {
  /** @type {(BundleSlot | null)[]} */
  #slots = [];

  /**
   * The bundle survives leaving the page (e.g. "Voeg toe aan bundel" on a PDP
   * links back here) via sessionStorage: per tab, gone when the tab closes, so
   * nobody returns days later to a stale half-built bundle. Saving starts only
   * once the saved bundle has been restored, so the empty first render can't
   * wipe it.
   */
  static #storageKey = 'primori:bundle-builder';

  #storageReady = false;

  #productCache = new Map();
  #productRequests = new Map();
  #initializing = true;
  #addingProduct = false;

  /**
   * Length of the tier ladder, NOT a cap on the bundle: the row pre-draws this many
   * slots because each advertises a tier, but a customer can add as many as they
   * like and everything past this keeps the top 40%.
   */
  #tierSlots = 5;

  /** Discount % indexed by (bundle size - 1): [0, 15, 25, 35, 40]. Whole numbers
   * only — Shopify's discount-code percentage field won't take a decimal. */
  #tiers = [0, 15, 25, 35, 40];

  #moneyFormat = '{{amount}}';

  #currency = 'EUR';

  #submitting = false;

  #preselectDone = false;

  /**
   * Picker filter/sort result: product handles matching the collection's
   * filters, in the chosen sort order, as returned by the collection itself
   * (#refreshPickerFilters). Null until the first result arrives — then every
   * card shows in render order.
   * @type {string[] | null}
   */
  #pickerOrder = null;

  /** Guards against a slow, older filter response overwriting a newer one. */
  #pickerRequest = 0;

  /** @type {{ first: string, next: string, max: string, savings: string, repeat: string }} */
  #i18n = {
    first: 'Voeg je eerste geur toe aan je bundel.',
    next: 'Voeg nog een geur toe en ontvang {pct}% KORTING.',
    max: 'Maximale korting bereikt — top!',
    savings: 'Je bespaart nu {amount}.',
    repeat: 'Nog een [title]',
  };

  connectedCallback() {
    super.connectedCallback();

    this.#tierSlots = Number(this.dataset.tierSlots) || 5;
    this.#tiers = (this.dataset.tiers ?? '')
      .split(',')
      .map((n) => Number(n.trim()))
      .filter((n) => !Number.isNaN(n));
    if (this.#tiers.length === 0) this.#tiers = [0, 15, 25, 35, 40];
    this.#moneyFormat = this.dataset.moneyformat || '{{amount}}';
    this.#currency = this.dataset.currency || 'EUR';
    this.#i18n = {
      first: this.dataset.i18nFirst || this.#i18n.first,
      next: this.dataset.i18nNext || this.#i18n.next,
      max: this.dataset.i18nMax || this.#i18n.max,
      savings: this.dataset.i18nSavings || this.#i18n.savings,
      repeat: this.dataset.i18nRepeat || this.#i18n.repeat,
    };

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('bundle-picker:filter', this.#onPickerFilter);

    this.#render();
    this.#applyPicker();
    this.#refreshPickerFilters(new URLSearchParams(window.location.search));
    // Restore + preselect must wait for all picker pages: a saved or deep-linked
    // product may sit on page 2+, which #loadRemainingProducts appends async.
    this.#loadRemainingProducts().then(async () => {
      await this.#restoreSlots();
      this.#storageReady = true;
      await this.#applyPreselect();
    }).catch((error) => {
      console.warn('Primori bundle initialization:', error);
    }).finally(() => {
      this.#initializing = false;
      this.#render();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('bundle-picker:filter', this.#onPickerFilter);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof HTMLElement)) return;

    // The filter bar's clear-all is a plain link to the collection (sort kept).
    // In the picker, clear in place instead of leaving the bundle page.
    const clearAll = target.closest('[data-picker-filters] a.facets-bar__clear');
    if (clearAll instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.#refreshPickerFilters(new URL(clearAll.href).searchParams);
      return;
    }

    if (this.#initializing || this.#submitting || this.#addingProduct) return;

    const productCard = target.closest('[data-bundle-product]');
    if (productCard instanceof HTMLElement) {
      this.#addProduct(productCard);
      return;
    }

    if (target.closest('[data-bundle-repeat]')) {
      this.#repeatLast();
      return;
    }

    if (target.closest('[data-bundle-choose]')) {
      this.#scrollToPicker();
      return;
    }

    const removeButton = target.closest('[data-bundle-remove]');
    if (removeButton) {
      const slot = removeButton.closest('.bundle-slot');
      if (slot instanceof HTMLElement) this.#removeProduct(Number(slot.dataset.slotIndex));
      return;
    }

    if (target.closest('[data-bundle-add]')) {
      this.#addBundleToCart();
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof HTMLElement)) return;

    // Cap-color pick inside a filled slot.
    if (target instanceof HTMLInputElement && target.hasAttribute('data-cap-input')) {
      const slot = target.closest('.bundle-slot');
      if (!(slot instanceof HTMLElement)) return;
      const entry = this.#slots[Number(slot.dataset.slotIndex)];
      if (!entry || this.#submitting || this.#initializing) {
        this.#render();
        return;
      }
      const product = this.#productCache.get(entry.handle);
      const variant = product && this.#variantForCap(product, target.value);
      if (!variant || !variant.available) {
        this.#render();
        return;
      }
      this.#setVariant(entry, product, variant);
      this.#render();
    }
  };

  /**
   * The embedded collection filters (facets.js) hand their parameters over
   * instead of navigating to the collection: "Toepassen", sort, pills, clear-all.
   * @param {Event} event
   */
  #onPickerFilter = (event) => {
    const { params } = /** @type {CustomEvent<{ params: URLSearchParams }>} */ (event).detail;
    this.#refreshPickerFilters(params);
  };

  /**
   * A slot entry built from a picker card — so price, title and image are always
   * the store's current ones. Null for a missing or sold-out product.
   * @param {HTMLElement | undefined} card
   * @returns {BundleSlot | null}
   */
  async #entryFromCard(card, requestedVariantId = '', capColor = '') {
    if (!card || !card.dataset.handle) return null;
    const product = await this.#loadProduct(card.dataset.handle);
    let variant;
    if (requestedVariantId) {
      variant = product.variants.find((item) => String(item.id) === String(requestedVariantId));
    } else if (capColor) {
      variant = this.#variantForCap(product, capColor);
    } else {
      variant = product.variants.find((item) => item.available && String(item.id) === card.dataset.variantId)
        || product.variants.find((item) => item.available);
    }
    if (!variant || !variant.available) return null;
    const entry = {
      handle: card.dataset.handle,
      variantId: '',
      title: product.title || card.dataset.title || '',
      price: 0,
      image: '',
      capColor: '',
    };
    this.#setVariant(entry, product, variant);
    return entry;
  }

  async #loadProduct(handle) {
    if (this.#productCache.has(handle)) return this.#productCache.get(handle);
    if (!this.#productRequests.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('Product kon niet worden geladen');
          return response.json();
        }).then((product) => {
          if (!Array.isArray(product.variants) || product.variants.length === 0) {
            throw new Error('Geen productvarianten ontvangen');
          }
          this.#productCache.set(handle, product);
          return product;
        }).finally(() => this.#productRequests.delete(handle));
      this.#productRequests.set(handle, request);
    }
    return this.#productRequests.get(handle);
  }

  #capIndex(product) {
    const index = (product.options || []).findIndex((option) =>
      /dop|cap|kleur|colou?r/i.test(typeof option === 'string' ? option : option.name || '')
    );
    return index < 0 ? 0 : index;
  }

  #capValue(product, variant) {
    const index = this.#capIndex(product);
    return String(variant.options?.[index] ?? variant[`option${index + 1}`] ?? '');
  }

  #normalizeCap(value) {
    const key = String(value || '').trim().toLocaleLowerCase('nl-NL');
    // Swatches use stable English keys; native variant values are Dutch.
    if (key === 'goud' || key === 'gold') return 'gold';
    if (key === 'zwart' || key === 'black') return 'black';
    return key;
  }

  #variantForCap(product, capColor) {
    const matches = product.variants.filter((variant) =>
      this.#normalizeCap(this.#capValue(product, variant)) === this.#normalizeCap(capColor)
    );
    return matches.find((variant) => variant.available) || matches[0];
  }

  #setVariant(entry, product, variant) {
    entry.variantId = String(variant.id);
    entry.price = Number(variant.price);
    entry.capColor = this.#capValue(product, variant);
    entry.image = variant.featured_image?.src || product.featured_image || product.images?.[0] || '';
  }

  /**
   * The picker card for a product handle. Matches on data-handle via a loop
   * (not a selector) so an untrusted handle — query string, storage — can't
   * inject into a selector.
   * @param {string} handle
   */
  #cardByHandle(handle) {
    if (!this.refs.picker) return undefined;
    return /** @type {HTMLElement[]} */ ([...this.refs.picker.querySelectorAll('[data-bundle-product]')]).find(
      (el) => el.dataset.handle === handle
    );
  }

  /** @param {HTMLElement} card */
  async #addProduct(card) {
    if (this.#addingProduct || this.#submitting) return;
    this.#addingProduct = true;
    this.#updateSummary();
    try {
      const entry = await this.#entryFromCard(card);
      if (!entry) throw new Error('Deze geur is niet beschikbaar');
      const index = this.#filledCount();
      this.#slots[index] = entry;
      this.#render();
      this.#scrollToTop();
      this.#scrollSlotIntoView(index);
    } catch (error) {
      console.warn('Primori bundle product:', error);
      window.alert('Deze geur kon niet worden toegevoegd. Probeer het opnieuw.');
    } finally {
      this.#addingProduct = false;
      this.#updateSummary();
    }
  }

  /**
   * Deep-link support: the PDP bundle-promo banner and the product cards link
   * here as `/pages/stel-je-eigen-bundel-samen?product=<handle>`. Adds that
   * product to the bundle — after any restored scents — then drops `product`
   * from the URL so a reload doesn't add it again. Runs once, after every
   * picker page is in the DOM.
   */
  async #applyPreselect() {
    if (this.#preselectDone) return;
    this.#preselectDone = true;

    const url = new URL(window.location.href);
    const handle = url.searchParams.get('product');
    const selectedVariantId = url.searchParams.get('bundle_variant');
    if (!handle) return;

    const card = this.#cardByHandle(handle);
    if (!card) return;
    // Resolve linked variants through the same path as picker and saved slots.
    try {
      const entry = await this.#entryFromCard(card, selectedVariantId || '');
      if (!entry) throw new Error('Selected cap is unavailable');
      const index = this.#filledCount();
      this.#slots[index] = entry;
      this.#render();
      this.#scrollToTop();
      this.#scrollSlotIntoView(index);
      url.searchParams.delete('product');
      url.searchParams.delete('bundle_variant');
      history.replaceState(history.state, '', url.toString());
    } catch (error) {
      console.warn('Primori bundle preselection:', error);
      window.alert('Deze geur met de gekozen dop kon niet worden toegevoegd. Kies de geur en dop opnieuw op deze pagina.');
    }
  }

  /** Saves the bundle (handle + cap colour per slot) for this tab. */
  #saveSlots() {
    if (!this.#storageReady) return;
    const saved = this.#slots.filter(Boolean).map((entry) => ({
      handle: entry?.handle ?? '',
      capColor: entry?.capColor ?? '',
      variantId: entry?.variantId ?? '',
    }));
    try {
      if (saved.length === 0) sessionStorage.removeItem(BundleBuilder.#storageKey);
      else sessionStorage.setItem(BundleBuilder.#storageKey, JSON.stringify(saved));
    } catch {
      // Storage blocked or full (private mode): the bundle just doesn't persist.
    }
  }

  /**
   * Restores the bundle saved for this tab. Each scent is rebuilt from its
   * current picker card, so a product that has sold out or gone since is
   * dropped rather than restored with stale data.
   */
  async #restoreSlots() {
    /** @type {unknown} */
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(BundleBuilder.#storageKey) ?? 'null');
    } catch {
      return;
    }
    if (!Array.isArray(saved)) return;

    /** @type {BundleSlot[]} */
    const entries = [];
    for (const item of saved) {
      if (!item || typeof item.handle !== 'string') continue;
      const entry = await this.#entryFromCard(
        this.#cardByHandle(item.handle),
        typeof item.variantId === 'string' ? item.variantId : '',
        typeof item.capColor === 'string' ? item.capColor : ''
      );
      if (entry) entries.push(entry);
    }
    this.#slots = entries;
  }

  /** Scroll the builder back into view so the just-filled slot + total are visible. */
  #scrollToTop() {
    // scroll-margin-top on .bundle-builder clears the sticky header.
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Track the row sideways to the slot just filled, so a bundle that has outgrown
   * the viewport doesn't drop the newest pick off-screen. Scrolls the row itself
   * rather than calling `scrollIntoView` on the slot, which would fight
   * `#scrollToTop` over the page's own scroll position.
   *
   * @param {number} index - Slot that should end up visible.
   */
  #scrollSlotIntoView(index) {
    const row = this.refs.slots;
    const slot = row.querySelectorAll('.bundle-slot:not([data-slot-add])')[index];
    if (!(slot instanceof HTMLElement)) return;

    // Aim past the slot at the "+" tile when it's out: landing with the next
    // affordance already visible saves a scroll to reach it.
    const tile = row.querySelector('[data-slot-add]');
    const target = tile instanceof HTMLElement && !tile.hidden ? tile : slot;

    const rowRect = row.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    // `padding-inline` on the row is 12px; clear it so the target isn't flush.
    const margin = 12;
    const overflowRight = targetRect.right - rowRect.right + margin;
    const overflowLeft = rowRect.left - targetRect.left + margin;

    if (overflowRight > 0) row.scrollBy({ left: overflowRight, behavior: 'smooth' });
    else if (overflowLeft > 0) row.scrollBy({ left: -overflowLeft, behavior: 'smooth' });
  }

  /**
   * "Nog een <geur>": adds the most recently added scent once more, with the
   * same cap colour (still changeable in its new slot), so the customer doesn't
   * have to scroll down to the picker for a second bottle of the same scent.
   */
  #repeatLast() {
    const count = this.#filledCount();
    const last = this.#slots[count - 1];
    if (!last) return;

    this.#slots[count] = { ...last };
    this.#render();
    this.#scrollSlotIntoView(count);
  }

  /** @param {number} index */
  #removeProduct(index) {
    if (Number.isNaN(index)) return;
    // Compact left so slot positions (and their tier ribbons) stay meaningful.
    this.#slots.splice(index, 1);
    this.#render();
  }

  #filledCount() {
    return this.#slots.filter(Boolean).length;
  }

  /**
   * Discount % the bundle currently earns, capped at the richest tier — a 6th item
   * sits on the same 40% as the 5th. Shared by the slot prices and the summary so
   * the two can't drift apart.
   */
  #tierPercent() {
    const count = this.#filledCount();
    if (count === 0) return 0;
    return this.#tiers[count - 1] ?? this.#tiers[this.#tiers.length - 1];
  }

  /**
   * A line's price at the current tier. Rounded per line, which is what Shopify
   * does at checkout — so these are the numbers that sum to the projected total.
   *
   * @param {number} price - Unit price in cents.
   */
  #discounted(price) {
    return Math.round(price * (1 - this.#tierPercent() / 100));
  }

  #scrollToPicker() {
    const picker = this.querySelector('.bundle-builder__picker');
    picker?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Applies the collection's filters + sort to the picker. Shopify only filters
   * and sorts on a collection URL, so ask the collection for the result through
   * its `bundle-picker` view (sections/bundle-picker-results.liquid), which
   * returns the collection's own filter bar + drawer (with active state and
   * counts) and the matching product handles in sort order. The bar replaces the
   * picker's; the handles drive which cards show and in what order.
   *
   * @param {URLSearchParams} params - Filter (`filter.*`) and `sort_by` params.
   */
  async #refreshPickerFilters(params) {
    const container = this.querySelector('[data-picker-filters]');
    if (!(container instanceof HTMLElement)) return;

    const query = new URLSearchParams();
    for (const [key, value] of params) {
      if (key.startsWith('filter.') || key === 'sort_by') query.append(key, value);
    }
    const request = ++this.#pickerRequest;
    const base = container.dataset.filtersUrl || '/collections/all';

    /** @param {number} page */
    const fetchPage = async (page) => {
      const url = new URL(base, window.location.origin);
      for (const [key, value] of query) url.searchParams.append(key, value);
      url.searchParams.set('view', 'bundle-picker');
      if (page > 1) url.searchParams.set('page', String(page));
      const html = await fetch(url.toString(), { credentials: 'same-origin' }).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const orderEl = doc.querySelector('[data-bundle-picker-order]');
      const order = orderEl ? JSON.parse(orderEl.textContent || '{}') : { pages: 0, handles: [] };
      return { doc, pages: Number(order.pages) || 1, handles: /** @type {string[]} */ (order.handles ?? []) };
    };

    try {
      const first = await fetchPage(1);
      const handles = [...first.handles];
      for (let page = 2; page <= first.pages; page++) {
        handles.push(...(await fetchPage(page)).handles);
      }
      if (request !== this.#pickerRequest) return;

      const filters = first.doc.querySelector('[data-bundle-picker-filters]');
      if (filters) {
        // The drawer lives inside the markup being replaced: close it first so its
        // scroll lock and focus trap are released properly.
        const drawer = container.querySelector('theme-drawer');
        if (drawer && 'close' in drawer && typeof drawer.close === 'function') drawer.close();
        container.replaceChildren(...[...filters.childNodes].map((node) => document.importNode(node, true)));
      }

      this.#pickerOrder = handles;
      this.#applyPicker();

      // Mirror the applied filters in the page URL: a reload or shared link
      // reopens the picker filtered, and facets.js reads the applied state from it.
      const pageUrl = new URL(window.location.href);
      for (const key of [...pageUrl.searchParams.keys()]) {
        if (key.startsWith('filter.') || key === 'sort_by') pageUrl.searchParams.delete(key);
      }
      for (const [key, value] of query) pageUrl.searchParams.append(key, value);
      history.replaceState(history.state, '', pageUrl.toString());
    } catch (error) {
      console.error('bundle-builder: failed to load picker filters', error);
    }
  }

  /**
   * The picker renders 50 products/page (Shopify's per-render max). Fetch the
   * remaining pages and append their cards so client-side filtering sees them all.
   */
  async #loadRemainingProducts() {
    const pages = Number(this.dataset.pickerPages) || 1;
    if (pages <= 1 || !this.refs.picker) return;

    for (let p = 2; p <= pages; p++) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(p));
        const html = await fetch(url.toString(), { credentials: 'same-origin' }).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.querySelector('bundle-builder .bundle-picker__grid');
        if (!grid) continue;
        const fragment = document.createDocumentFragment();
        grid.querySelectorAll('[data-bundle-product]').forEach((card) => {
          fragment.appendChild(document.importNode(card, true));
        });
        this.refs.picker.appendChild(fragment);
      } catch (error) {
        console.error('bundle-builder: failed to load picker page', p, error);
      }
    }

    // Re-number data-index so "featured" order stays sequential across pages.
    [...this.refs.picker.querySelectorAll('[data-bundle-product]')].forEach((card, i) => {
      card.dataset.index = String(i);
    });
    this.#applyPicker();
  }

  /**
   * Shows the cards in the collection's filter result, in its sort order, and
   * hides the rest (#pickerOrder; see #refreshPickerFilters). Before the first
   * result arrives every card shows in render order. The product count lives
   * in the collection's filter bar, which comes back with the result.
   */
  #applyPicker() {
    if (!this.refs.picker) return;
    const cards = /** @type {HTMLElement[]} */ ([...this.refs.picker.querySelectorAll('[data-bundle-product]')]);
    if (cards.length === 0) return;

    const order = this.#pickerOrder;
    /** @type {Map<string, number>} */
    const rank = new Map((order ?? []).map((handle, i) => [handle, i]));

    let visible = 0;
    for (const card of cards) {
      const show = order === null || rank.has(card.dataset.handle ?? '');
      card.hidden = !show;
      if (show) visible++;
    }

    // Reorder the DOM to the result's order; hidden cards keep render order after.
    const position = (/** @type {HTMLElement} */ card) =>
      rank.get(card.dataset.handle ?? '') ?? order?.length ?? 0;
    const sorted = [...cards].sort(
      (a, b) => position(a) - position(b) || (Number(a.dataset.index) || 0) - (Number(b.dataset.index) || 0)
    );
    for (const card of sorted) this.refs.picker.appendChild(card);

    const noResults = this.querySelector('[data-picker-noresults]');
    if (noResults instanceof HTMLElement) noResults.hidden = visible !== 0;
  }

  #render() {
    const count = this.#filledCount();
    this.#syncExtraSlots(count);
    this.#syncAddTile(count);

    // Excludes the trailing "add another" tile — it holds no product.
    const slotEls = /** @type {HTMLElement[]} */ ([
      ...this.refs.slots.querySelectorAll('.bundle-slot:not([data-slot-add])'),
    ]);

    slotEls.forEach((slotEl, index) => {
      slotEl.dataset.slotIndex = String(index);
      const entry = this.#slots[index];

      if (entry) {
        this.#fillSlot(slotEl, index, entry);
      } else {
        this.#emptySlot(slotEl);
      }
    });

    this.#syncRepeat(count, slotEls);
    this.#updateSummary();
    this.#saveSlots();
  }

  /**
   * Shows "Nog een <geur>" on the next open place only — the first empty tiered
   * slot, or the "+" tile once the ladder is full — labelled with the most
   * recently added scent. Hidden while the bundle is empty.
   *
   * @param {number} count - Products currently in the bundle.
   * @param {HTMLElement[]} slotEls - The product slots, in order.
   */
  #syncRepeat(count, slotEls) {
    const last = count > 0 ? this.#slots[count - 1] : null;
    const next = count < this.#tierSlots ? slotEls[count] : this.refs.slots.querySelector('[data-slot-add]');
    const target = next?.querySelector('[data-bundle-repeat]') ?? null;

    for (const button of this.refs.slots.querySelectorAll('[data-bundle-repeat]')) {
      if (!(button instanceof HTMLElement)) continue;
      const show = button === target && last !== null;
      button.hidden = !show;
      if (show && last) {
        button.textContent = `${this.#i18n.repeat.replace('[title]', last.title)} +`;
      }
    }
  }

  /**
   * Add or drop the slots past the tier ladder so the row holds exactly as many as
   * the bundle does. They're plain by design: the discount is already at its cap
   * there, so there's no tier to put on a ribbon and no "Vanaf" price to quote.
   * Each gets the same "+" separator the pre-drawn slots have between them.
   *
   * @param {number} count - Products currently in the bundle.
   */
  #syncExtraSlots(count) {
    const container = this.refs.slots;
    const addTile = container.querySelector('[data-slot-add]');
    const extras = /** @type {HTMLElement[]} */ ([...container.querySelectorAll('[data-slot-extra]')]);
    const needed = Math.max(0, count - this.#tierSlots);

    // Trim from the end: #slots compacts left on removal, so the last slot is
    // always the one that goes.
    for (let i = extras.length; i > needed; i--) {
      const slot = extras[i - 1];
      slot.previousElementSibling?.remove();
      slot.remove();
    }

    for (let i = extras.length; i < needed; i++) {
      const separator = document.createElement('span');
      separator.className = 'bundle-builder__plus';
      separator.setAttribute('aria-hidden', 'true');
      separator.textContent = '+';

      const slot = document.createElement('div');
      slot.className = 'bundle-slot bundle-slot--extra';
      slot.dataset.slotExtra = '';
      slot.dataset.state = 'empty';
      slot.setAttribute('role', 'listitem');

      container.insertBefore(separator, addTile);
      container.insertBefore(slot, addTile);
    }
  }

  /**
   * The blank "+" tile only earns its place once the ladder is full; before that
   * every empty slot already carries its own "choose a scent" button.
   *
   * @param {number} count - Products currently in the bundle.
   */
  #syncAddTile(count) {
    const addTile = this.refs.slots.querySelector('[data-slot-add]');
    if (addTile instanceof HTMLElement) addTile.hidden = count < this.#tierSlots;
  }

  /**
   * @param {HTMLElement} slotEl
   * @param {number} index
   * @param {BundleSlot} entry
   */
  #fillSlot(slotEl, index, entry) {
    let filled = slotEl.querySelector('[data-slot-filled]');

    if (!filled) {
      const fragment = this.refs.filledTemplate.content.cloneNode(true);
      filled = /** @type {HTMLElement} */ (fragment).querySelector('[data-slot-filled]');
      if (!(filled instanceof HTMLElement)) return;
      this.#uniquifyCapInputs(filled, index);
      slotEl.appendChild(filled);
    }

    const img = filled.querySelector('[data-slot-image]');
    if (img instanceof HTMLImageElement) {
      if (entry.image) {
        img.src = entry.image;
        img.alt = entry.title;
        img.hidden = false;
      } else {
        img.removeAttribute('src');
        img.hidden = true;
      }
    }

    const title = filled.querySelector('[data-slot-title]');
    if (title) title.textContent = entry.title;

    // Re-priced on every render, so adding or removing a product restates every
    // slot at the tier the bundle has just moved to.
    const price = filled.querySelector('[data-slot-price]');
    if (price) price.textContent = this.#money(this.#discounted(entry.price));

    const priceWas = filled.querySelector('[data-slot-price-was]');
    if (priceWas instanceof HTMLElement) {
      priceWas.textContent = this.#money(entry.price);
      priceWas.hidden = this.#tierPercent() === 0;
    }

    // This markup is reused in place, so after a removal the element can be showing
    // a different product than it was a moment ago while its radios still hold the
    // previous occupant's colour. Drive the radios from the entry rather than the
    // other way round; fall back to whatever is checked only when the entry has no
    // colour yet (a fresh pick, adopting the markup's default).
    const capInputs = /** @type {HTMLInputElement[]} */ ([...filled.querySelectorAll('[data-cap-input]')]);
    const product = this.#productCache.get(entry.handle);
    for (const input of capInputs) {
      const variant = product && this.#variantForCap(product, input.value);
      input.checked = this.#normalizeCap(input.value) === this.#normalizeCap(entry.capColor);
      input.disabled = !variant?.available || this.#submitting;
      input.dataset.variantId = variant ? String(variant.id) : '';
      const label = input.closest('label');
      if (label) {
        label.setAttribute('aria-disabled', String(input.disabled));
        label.style.opacity = variant?.available ? '' : '0.45';
      }
    }

    slotEl.dataset.state = 'filled';
  }

  /** @param {HTMLElement} slotEl */
  #emptySlot(slotEl) {
    slotEl.querySelector('[data-slot-filled]')?.remove();
    slotEl.dataset.state = 'empty';
  }

  /**
   * Radio `name`/`id` collide across cloned slots; scope them per slot so a
   * selection in one slot doesn't clear another.
   * @param {HTMLElement} filled
   * @param {number} index
   */
  #uniquifyCapInputs(filled, index) {
    const suffix = `-slot-${index}`;

    filled.querySelectorAll('input[data-cap-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const oldId = input.id;
      input.name += suffix;
      if (oldId) {
        const newId = oldId + suffix;
        input.id = newId;
        const label = filled.querySelector(`label[for="${oldId}"]`);
        if (label) label.setAttribute('for', newId);
      }
    });

    const group = filled.querySelector('[role="radiogroup"]');
    const labelledby = group?.getAttribute('aria-labelledby');
    if (group && labelledby) {
      const heading = filled.querySelector(`#${CSS.escape(labelledby)}`);
      const newId = labelledby + suffix;
      if (heading) heading.id = newId;
      group.setAttribute('aria-labelledby', newId);
    }
  }

  #updateSummary() {
    const count = this.#filledCount();
    const pct = this.#tierPercent();
    const original = this.#slots.reduce((sum, s) => sum + (s ? s.price : 0), 0);
    // Sum of the per-line prices the slots are showing, rather than a percentage of
    // the summed total: the two differ by a cent at some tiers, and per line is both
    // what the row displays and how Shopify rounds a percentage discount at checkout
    // (2 items at 15% off €11,45: €19,46 per line vs €19,47 on the sum).
    const discounted = this.#slots.reduce((sum, s) => sum + (s ? this.#discounted(s.price) : 0), 0);
    const savings = original - discounted;

    const countEl = this.querySelector('[data-bundle-count]');
    if (countEl) countEl.textContent = String(count);

    // "7/5" reads as an error; past the ladder the count stands on its own.
    const countMaxEl = this.querySelector('[data-bundle-count-max]');
    if (countMaxEl instanceof HTMLElement) countMaxEl.hidden = count > this.#tierSlots;

    this.querySelectorAll('[data-bundle-dot]').forEach((dot, i) => {
      dot.toggleAttribute('data-active', i < count);
    });

    const originalEl = this.querySelector('[data-bundle-original]');
    if (originalEl instanceof HTMLElement) {
      if (pct > 0) {
        originalEl.textContent = this.#money(original);
        originalEl.hidden = false;
      } else {
        originalEl.hidden = true;
      }
    }

    const totalEl = this.querySelector('[data-bundle-total]');
    if (totalEl) totalEl.textContent = this.#money(discounted);

    const upsell = this.querySelector('[data-bundle-upsell]');
    if (upsell instanceof HTMLElement) upsell.innerHTML = this.#upsellText(count, savings);

    const addButton = this.querySelector('[data-bundle-add]');
    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = count < 1 || this.#submitting || this.#initializing || this.#addingProduct;
    }
  }

  /**
   * @param {number} count
   * @param {number} savings
   */
  #upsellText(count, savings) {
    if (count === 0) return this.#i18n.first;

    const savingsText =
      savings > 0 ? ` ${this.#i18n.savings.replace('{amount}', `<strong>${this.#money(savings)}</strong>`)}` : '';

    // Top tier reached: nothing left to unlock, but still show what they save.
    if (count >= this.#tierSlots) return this.#i18n.max + savingsText;

    const nextPct = this.#tiers[count] ?? 0;
    // The locale string keeps the literal "%" after {pct}, so insert only the number.
    return this.#i18n.next.replace('{pct}', `<strong>${nextPct}</strong>`) + savingsText;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.#moneyFormat, this.#currency);
  }

  async #addBundleToCart() {
    const items = /** @type {BundleSlot[]} */ (this.#slots.filter(Boolean));
    if (items.length === 0 || this.#submitting || this.#initializing || this.#addingProduct) return;

    this.#submitting = true;
    this.#updateSummary();

    const bundleId = this.#bundleId();
    const cartSectionIds = this.#cartSectionIds();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.variantId, quantity: 1 })),
        promise: deferred.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: 1,
        properties: {
          // Provenance, not enforcement: the tier discount codes work off
          // quantity, so nothing groups by this any more. Kept because it's the
          // only way to tell on the order which lines were bought as one bundle
          // (gift box, picking).
          _bundle_id: bundleId,
        },
      })),
      sections: cartSectionIds.join(','),
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        const cart = await this.#refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'bundle-builder', sourceId: this.id.toString() },
        });
        return;
      }

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'bundle-builder',
          sourceId: this.id.toString(),
          sections: response.sections,
        },
      });

      this.#openCartDrawer();

      // Bundle handed off to the cart; reset the builder for the next one.
      this.#slots = [];
      this.#render();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#submitting = false;
      this.#updateSummary();
    }
  }

  // The theme's <cart-drawer auto-open> event listener isn't guaranteed to be
  // present, so open the drawer explicitly — same path as standard-actions-override.
  #openCartDrawer() {
    /** @type {any} */
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  #bundleId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `bundle-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
  }

  #cartSectionIds() {
    /** @type {string[]} */
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  async #refreshCart() {
    /** @type {any} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
